import React from 'react'
import { useTranslation } from 'react-i18next'

import { NavigationProgressBarProps } from '../../../../../@types/props-types'
import { Screen } from '../../../../../@types/types'
import {
  AbstractScreenValidationResult,
  ScreenValidationResult,
  ValidationResult,
} from '../../../../../@types/validation-types'
import { camelCaseToDash, underlineToDash } from '../../../../../utils/text/text-utils'
import SidenavItem from '../sidenavItem/SidenavItem'
import { FormStateFeatureState } from '../../../../../@types/state-types'
import { ScreenName } from '../../../../../services/domain/wizardScreens'
import { useFormState } from '../../wizard/useFormState'
import { hasErrors } from '../../../../../services/validation/editor/runValidation'

const getIcon = (
  validationMessages: ScreenValidationResult<AbstractScreenValidationResult>,
  screenName: string,
  formState: FormStateFeatureState,
  scrolled: boolean
) => {
  if (hasErrors(validationMessages)) {
    return formState.submitCount > 0 || formState.touchedScreen[screenName as ScreenName] ? 'info' : undefined
  }

  if (scrolled || formState.submitCount > 0) {
    return 'accept'
  }
  return undefined
}

const NavigationProgressBarBody: React.FunctionComponent<NavigationProgressBarProps> = ({
  screenNo,
  screens,
  validationMessages,
  onNavigate,
}) => {
  const { t } = useTranslation()
  const { formState } = useFormState()
  const lastScreen = screens[screens.length - 1]

  return (
    <>
      {screens.map((screen: Screen, index) => {
        const screenValidationMessages = validationMessages[screen.name as keyof ValidationResult]
        const elementId = `navigation-progress-bar-${underlineToDash(camelCaseToDash(screen.name))}`
        const onClick = () => {
          onNavigate(screen)
        }

        // hack to mark the second last screen as scrolled when the last screen is scrolled
        // the last screen is having another InView margin, so it's marked when the bottom of the page is reached
        // to not have also a specific margins on the second last screen
        // (difficult to define because this screen is often flow specific)
        // we mark it as scrolled when the last screen is scrolled
        const scrolled =
          formState.scrolled[screen.name as ScreenName] === true ||
          (index === screens.length - 2 && formState.scrolled[lastScreen.name as ScreenName] === true)

        return (
          <SidenavItem
            key={elementId}
            data-testid={elementId}
            id={elementId}
            isSelected={index === screenNo}
            label={t(screen.translation, screen.defaultTranslation)}
            icon={getIcon(screenValidationMessages, screen.name, formState, scrolled)}
            linkProps={{
              to: screen.id,
              spy: true,
              smooth: true,
              offset: index === 0 ? -23 : -35,
              duration: 700,
              activeClass: 'active',
              onClick: onClick,
            }}
          />
        )
      })}
    </>
  )
}

export default NavigationProgressBarBody
